import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { checkShiftOverlap, formatOverlapDescription, ShiftForOverlap } from "@/utils/shiftOverlapUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogPortal,
  AlertDialogOverlay,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useSchedulingMutations } from "@/hooks/useSchedulingMutations";
import { useOfferShift } from "@/hooks/schedule/useOfferShift";
import { useCancelOffer } from "@/hooks/schedule/useCancelOffer";
import { useCompanySettings, isOffersEnabled, isSwapsEnabled } from "@/hooks/useCompanySettings";
import { useAuth } from "@/hooks/useAuth";

interface Shift {
  id: string;
  profile_id: string | null;
  start_time: string;
  end_time: string;
  note: string | null;
  status?: 'active' | 'open';
  employee_name: string;
  offered_by_user_id?: string | null;
  offered_by_name?: string | null;
}

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  status: string;
  company_id: string;
}

interface ShiftEditDialogProps {
  shift: Shift | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShiftUpdated: (updatedShift: Shift) => void;
  onShiftCancelled: (shiftId: string) => void;
  onShiftOffered?: (shiftId: string) => void;
  cancelShiftMutation?: any;
  editShiftMutation?: any;
  profiles: Array<{ id: string; first_name?: string; last_name?: string; full_name?: string }>;
  confirmBeforeCancel: boolean;
  onChangeConfirmBeforeCancel: (enabled: boolean) => void;
  existingShifts?: ShiftForOverlap[];
}

const schema = z.object({
  profile_id: z.string().uuid().nullable().optional(), // null => open shift
  start_time: z.date(),
  end_time: z.date(),
  note: z.string().max(1000).optional().nullable(),
}).refine(v => v.start_time < v.end_time, {
  message: 'End time must be after start time',
  path: ['end_time'],
});

type FormData = z.infer<typeof schema>;

export function ShiftEditDialog({ 
  shift, 
  open, 
  onOpenChange, 
  onShiftUpdated, 
  onShiftCancelled,
  onShiftOffered,
  cancelShiftMutation,
  editShiftMutation,
  profiles,
  confirmBeforeCancel,
  onChangeConfirmBeforeCancel,
  existingShifts = []
}: ShiftEditDialogProps) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [showOverlapWarning, setShowOverlapWarning] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);
  const [overlapDescription, setOverlapDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  // isCancellingOffer state now managed by useCancelOffer mutation
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const isMobile = useIsMobile();
  
  const { editShift, cancelShift } = useSchedulingMutations();
  const offerShiftMutation = useOfferShift();
  const cancelOfferMutation = useCancelOffer();
  const isOfferingShift = offerShiftMutation.isPending;
  const isCancellingOfferMutation = cancelOfferMutation.isPending;
  const { settings: companySettings } = useCompanySettings();
  const { profile } = useAuth();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      profile_id: "",
      start_time: new Date(),
      end_time: new Date(),
      note: "",
    },
  });

  // Set form values when shift changes
  useEffect(() => {
    if (shift && open) {
      form.reset({
        profile_id: shift.profile_id,
        start_time: new Date(shift.start_time),
        end_time: new Date(shift.end_time),
        note: shift.note || "",
      });
    }
  }, [shift, open, form]);

  const onSubmit = async (data: FormData) => {
    if (!shift) return;

    // Check for overlapping shifts (exclude the current shift being edited)
    const overlapResult = checkShiftOverlap(
      {
        profile_id: data.profile_id ?? null,
        start_time: data.start_time.toISOString(),
        end_time: data.end_time.toISOString(),
      },
      existingShifts,
      shift.id // Exclude current shift from overlap check
    );

    if (overlapResult.hasOverlap) {
      const desc = formatOverlapDescription(overlapResult.overlappingShifts);
      setOverlapDescription(desc);
      setPendingFormData(data);
      setShowOverlapWarning(true);
      return;
    }

    await executeSubmit(data);
  };

  const executeSubmit = async (data: FormData) => {
    if (!shift) return;

    setIsSubmitting(true);
    try {
      if (editShiftMutation) {
        editShiftMutation.mutate({
          shiftId: shift.id,
          profileId: data.profile_id,
          start: data.start_time,
          end: data.end_time,
          note: data.note || null,
        });
        
        onOpenChange(false);
      } else {
        const updatedShiftData = await editShift({
          shiftId: shift.id,
          profileId: data.profile_id,
          start: data.start_time,
          end: data.end_time,
          note: data.note || null,
        });

        const selectedProfile = data.profile_id ? profiles.find(p => p.id === data.profile_id) : null;
        const enrichedShift: Shift = {
          ...updatedShiftData,
          status: (updatedShiftData.status as 'active' | 'open') || 'active',
          employee_name: selectedProfile 
            ? `${selectedProfile.first_name} ${selectedProfile.last_name}`.trim()
            : 'Open shift'
        };

        onShiftUpdated(enrichedShift);
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error updating shift:', error);
    } finally {
      setIsSubmitting(false);
      setShowOverlapWarning(false);
      setPendingFormData(null);
    }
  };

  const handleConfirmOverlap = async () => {
    if (pendingFormData) {
      await executeSubmit(pendingFormData);
    }
  };

  const handleCancelShift = async () => {
    if (!shift) return;

    setIsCancelling(true);
    try {
      if (dontAskAgain) {
        await onChangeConfirmBeforeCancel(false);
      }
      
      // FIX: Use React Query mutation if available, fallback to old method
      if (cancelShiftMutation) {
        cancelShiftMutation.mutate(shift.id);
      } else {
        await cancelShift(shift.id);
        onShiftCancelled(shift.id);
      }
      
      // FIX: Close dialogs after successful cancellation
      setShowCancelDialog(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Error cancelling shift:', error);
      // Keep dialogs open on error so user can retry
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelClick = () => {
    if (!confirmBeforeCancel) {
      handleCancelShift();
    } else {
      setShowCancelDialog(true);
    }
  };

  const handleOfferShift = () => {
    if (!shift) return;
    offerShiftMutation.mutate(shift.id, {
      onSuccess: () => {
        onShiftOffered?.(shift.id);
        onOpenChange(false);
        setShowOfferDialog(false);
      },
    });
  };

  const handleCancelOffer = () => {
    if (!shift) return;
    cancelOfferMutation.mutate(shift.id, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const formatDateTimeLocal = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeLocal = (value: string) => {
    return new Date(value);
  };

  if (!shift) return null;

  const NONE_VALUE = "__NONE__";
  
  // Helper function to display profile name
  const displayName = (profile: { first_name?: string; last_name?: string; full_name?: string }) => {
    const full = (profile.full_name ?? "").trim();
    if (full) return full;
    const fallback = [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim();
    return fallback || "Unnamed";
  };

  // Form body component (shared between Dialog and Drawer)
  const formBody = (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="profile_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Employee</FormLabel>
            <div className="space-y-2">
              <Select 
                onValueChange={(value) => field.onChange(value === NONE_VALUE ? null : value)}
                value={field.value ?? NONE_VALUE}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-[80]">
                  <SelectItem value={NONE_VALUE}>None (Open shift)</SelectItem>
                  {profiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {displayName(profile)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isOffersEnabled(companySettings) && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => field.onChange(null)}
                  className="h-auto p-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  Unassign (make open)
                </Button>
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="start_time"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Start Time</FormLabel>
            <FormControl>
              <Input
                type="datetime-local"
                value={formatDateTimeLocal(field.value)}
                onChange={(e) => field.onChange(parseDateTimeLocal(e.target.value))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="end_time"
        render={({ field }) => (
          <FormItem>
            <FormLabel>End Time</FormLabel>
            <FormControl>
              <Input
                type="datetime-local"
                value={formatDateTimeLocal(field.value)}
                onChange={(e) => field.onChange(parseDateTimeLocal(e.target.value))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="note"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Note (Optional)</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Add a note about this shift..."
                {...field}
                value={field.value || ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );

  // Button group component (shared between Dialog and Drawer)
  const buttonGroup = (
    <>
      {isOffersEnabled(companySettings) && 
       shift.profile_id === profile?.id && 
       shift.status === 'active' && (
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowOfferDialog(true)}
          disabled={isSubmitting || isCancelling || isOfferingShift || isCancellingOfferMutation}
        >
          Offer Shift
        </Button>
      )}
      {isOffersEnabled(companySettings) && 
       shift.offered_by_user_id && 
       shift.offered_by_user_id === profile?.id && (
        <Button
          type="button"
          variant="outline"
          onClick={handleCancelOffer}
          disabled={isSubmitting || isCancelling || isOfferingShift || isCancellingOfferMutation}
        >
          {isCancellingOfferMutation ? "Cancelling..." : "Cancel Offer"}
        </Button>
      )}
      <Button
        type="button"
        variant="destructive"
        onClick={handleCancelClick}
        disabled={isSubmitting || isCancelling || isCancellingOfferMutation}
      >
        Cancel Shift
      </Button>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isSubmitting || isCancelling || isCancellingOfferMutation}
        >
          Close
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || isCancelling || isCancellingOfferMutation || !form.formState.isValid}
        >
          {isSubmitting ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </>
  );

  if (isMobile) {
    // Mobile: bottom sheet Drawer (safer for small viewports)
    return (
      <>
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="z-[60]">
            <DrawerHeader>
              <DrawerTitle>Edit Shift</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pb-4">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  {formBody}
                </form>
              </Form>
            </div>
            <DrawerFooter className="px-4">
              <div className="flex flex-col gap-2">
                {buttonGroup}
              </div>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <AlertDialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
            <AlertDialogOverlay className="fixed inset-0 bg-black/60 z-[70]" />
            <AlertDialogContent className="z-[80] sm:max-w-[420px]">
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel Shift</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to cancel this shift for {shift.employee_name}? 
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="px-6 pb-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={dontAskAgain}
                    onChange={(e) => setDontAskAgain(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span>Don't show this again</span>
                </label>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isCancelling}>
                  Keep Shift
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleCancelShift}
                  disabled={isCancelling}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isCancelling ? "Cancelling..." : "Cancel Shift"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogPortal>
        </AlertDialog>

        <AlertDialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
          <AlertDialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
            <AlertDialogOverlay className="fixed inset-0 bg-black/60 z-[70]" />
            <AlertDialogContent className="z-[80] sm:max-w-[420px]">
              <AlertDialogHeader>
                <AlertDialogTitle>Offer this shift?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your shift will appear in Open Shifts as 'Offered by {profile?.first_name} {profile?.last_name}'. Anyone eligible can claim it.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isOfferingShift}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleOfferShift}
                  disabled={isOfferingShift}
                  className="flex items-center gap-2"
                >
                  {isOfferingShift && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isOfferingShift ? 'Offering...' : 'Confirm Offer'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogPortal>
        </AlertDialog>

        <AlertDialog open={showOverlapWarning} onOpenChange={setShowOverlapWarning}>
          <AlertDialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
            <AlertDialogOverlay className="fixed inset-0 bg-black/60 z-[70]" />
            <AlertDialogContent className="z-[80] sm:max-w-[420px]">
              <AlertDialogHeader>
                <AlertDialogTitle>Overlapping Shift Detected</AlertDialogTitle>
                <AlertDialogDescription>
                  This employee already has a shift during this time: {overlapDescription}. Do you want to save anyway?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => { setShowOverlapWarning(false); setPendingFormData(null); }}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirmOverlap}>
                  Save Anyway
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogPortal>
        </AlertDialog>
      </>
    );
  }

  // Desktop: Dialog via Portal → body (high z-index to beat sticky headers)
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
          <DialogOverlay className="fixed inset-0 z-[60] bg-black/50" />
          <DialogContent className="z-[60] sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>Edit Shift</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                {formBody}
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  {buttonGroup}
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
          <AlertDialogOverlay className="fixed inset-0 bg-black/60 z-[70]" />
          <AlertDialogContent className="z-[80] sm:max-w-[420px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Shift</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel this shift for {shift.employee_name}? 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="px-6 pb-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={dontAskAgain}
                  onChange={(e) => setDontAskAgain(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span>Don't show this again</span>
              </label>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isCancelling}>
                Keep Shift
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelShift}
                disabled={isCancelling}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isCancelling ? "Cancelling..." : "Cancel Shift"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>

      <AlertDialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <AlertDialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
          <AlertDialogOverlay className="fixed inset-0 bg-black/60 z-[70]" />
          <AlertDialogContent className="z-[80] sm:max-w-[420px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Offer this shift?</AlertDialogTitle>
              <AlertDialogDescription>
                Your shift will appear in Open Shifts as 'Offered by {profile?.first_name} {profile?.last_name}'. Anyone eligible can claim it.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isOfferingShift}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleOfferShift}
                disabled={isOfferingShift}
                className="flex items-center gap-2"
              >
                {isOfferingShift && <Loader2 className="h-4 w-4 animate-spin" />}
                {isOfferingShift ? 'Offering...' : 'Confirm Offer'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>

      <AlertDialog open={showOverlapWarning} onOpenChange={setShowOverlapWarning}>
        <AlertDialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
          <AlertDialogOverlay className="fixed inset-0 bg-black/60 z-[70]" />
          <AlertDialogContent className="z-[80] sm:max-w-[420px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Overlapping Shift Detected</AlertDialogTitle>
              <AlertDialogDescription>
                This employee already has a shift during this time: {overlapDescription}. Do you want to save anyway?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => { setShowOverlapWarning(false); setPendingFormData(null); }}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmOverlap}>
                Save Anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>
    </>
  );
}
